import { useState, useEffect } from 'react';
import {
  Calculator, Target, Shield, AlertTriangle, TrendingUp, TrendingDown,
  Loader2, Info, Sparkles,
} from 'lucide-react';
import { calculatePositionSize } from '../services/api';
import LeverageTierBadge from '../components/LeverageTierBadge';
import { InfoTip } from '../components/ui';

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export default function PositionSizingPage() {
  const [input, setInput] = useState({
    accountBalance: 10000,
    riskPct: 1,
    entryPrice: 50000,
    stopLossPrice: 49000,
    takeProfitPrice: 52000,
    side: 'long',
    leverage: 1,
    winRate: 0.55,
    avgWinLossRatio: 2,
    showKelly: false,
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto-calc on input change (debounced)
  useEffect(() => {
    setError(null);
    const t = setTimeout(async () => {
      // Skip if obviously invalid
      if (!input.accountBalance || !input.entryPrice || !input.stopLossPrice) {
        setResult(null);
        return;
      }
      setLoading(true);
      try {
        const payload = {
          accountBalance: parseFloat(input.accountBalance),
          riskPct: parseFloat(input.riskPct),
          entryPrice: parseFloat(input.entryPrice),
          stopLossPrice: parseFloat(input.stopLossPrice),
          side: input.side,
          leverage: parseFloat(input.leverage) || 1,
        };
        // Take profit is optional — only include if user typed a value
        const tp = parseFloat(input.takeProfitPrice);
        if (Number.isFinite(tp) && tp > 0) {
          payload.takeProfitPrice = tp;
        }
        if (input.showKelly) {
          payload.winRate = parseFloat(input.winRate);
          payload.avgWinLossRatio = parseFloat(input.avgWinLossRatio);
        }
        const res = await calculatePositionSize(payload);
        setResult(res?.data || res);
      } catch (err) {
        setError(err.message);
        setResult(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [input]);

  const set = (k) => (e) => setInput(c => ({ ...c, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="w-7 h-7 text-cyan-400" />
          <h1 className="text-2xl font-bold text-white">Position Sizing Calculator</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Free Tool</span>
        </div>
        <p className="text-sm text-gray-400">
          Industry-standard risk-based position sizing. Pure math, no execution. <span className="text-emerald-400">Use this BEFORE every trade to control your risk per position.</span>
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Inputs */}
        <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Target className="w-4 h-4 text-cyan-400" /> Trade parameters
          </h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Account balance ($)</label>
              <input type="number" step="any" value={input.accountBalance} onChange={set('accountBalance')}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                Risk per trade (%)
                <InfoTip side="top">% of account you lose if stop hits. 1% means 20 losers in a row leaves you at ~82% of starting capital. Above 5% = gambling.</InfoTip>
              </label>
              <input type="number" step="0.1" value={input.riskPct} onChange={set('riskPct')}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
              <p className="text-[10px] text-gray-500 mt-1">Standard: 0.5–2%. Aggressive: 3–5%.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setInput(c => ({ ...c, side: 'long' }))}
              className={`p-2.5 rounded-lg text-sm font-bold border flex items-center justify-center gap-1 ${input.side === 'long' ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>
              <TrendingUp className="w-4 h-4" /> LONG
            </button>
            <button onClick={() => setInput(c => ({ ...c, side: 'short' }))}
              className={`p-2.5 rounded-lg text-sm font-bold border flex items-center justify-center gap-1 ${input.side === 'short' ? 'bg-red-500/15 border-red-400/40 text-red-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>
              <TrendingDown className="w-4 h-4" /> SHORT
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Entry price ($)</label>
              <input type="number" step="any" value={input.entryPrice} onChange={set('entryPrice')}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Stop-loss price ($)</label>
              <input type="number" step="any" value={input.stopLossPrice} onChange={set('stopLossPrice')}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">
              Take-profit price ($) <span className="text-gray-600">— optional, enables R:R math</span>
            </label>
            <input type="number" step="any" value={input.takeProfitPrice} onChange={set('takeProfitPrice')}
              placeholder="Leave blank to skip"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 flex items-center gap-1">
              Leverage ({input.leverage}x)
              <InfoTip side="top">Does NOT multiply expected return — only changes required margin. 1x = spot. 3-10x = swing futures. 25x+ = intraday only. Liquidation risk grows with leverage.</InfoTip>
            </label>
            <input type="range" min="1" max="125" value={input.leverage} onChange={set('leverage')} className="w-full" />
            <div className="mt-2">
              <LeverageTierBadge leverage={parseFloat(input.leverage)} compact />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
            <input type="checkbox" checked={input.showKelly} onChange={set('showKelly')} className="rounded" />
            Show Kelly Criterion suggestion (advanced)
          </label>

          {input.showKelly && (
            <div className="grid grid-cols-2 gap-3 bg-dark-700/30 rounded-lg p-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Historical win rate (0–1)</label>
                <input type="number" step="0.01" min="0" max="1" value={input.winRate} onChange={set('winRate')}
                  className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-xs font-mono" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Avg win/loss ratio</label>
                <input type="number" step="0.1" value={input.avgWinLossRatio} onChange={set('avgWinLossRatio')}
                  className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-xs font-mono" />
              </div>
            </div>
          )}
        </div>

        {/* Result */}
        <div className="space-y-4">
          {error && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 text-red-400 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!result && !error ? (
            <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
              <Calculator className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Adjust parameters to see position size.</p>
            </div>
          ) : result && (
            <>
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5">
                <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">Position size</p>
                <p className="text-3xl font-bold text-white font-mono">
                  {formatNum(result.positionSizeBase, result.positionSizeBase >= 1 ? 4 : 8)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  base units = {formatNum(result.notionalValue, 2)} USD notional
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Stat label="Max risk ($)" value={`$${formatNum(result.maxRiskDollars, 2)}`} accent="amber" />
                <Stat label="Stop distance" value={`${formatNum(result.stopDistancePct, 2)}%`} />
                <Stat label="Margin required" value={`$${formatNum(result.marginRequired, 2)}`} />
                <Stat label="Leveraged exposure" value={`${formatNum(result.leveragedExposurePct, 1)}%`} accent={result.leveragedExposurePct > 100 ? 'red' : undefined} />
                {/* Liquidation tiles only render for leveraged positions.
                    Spot (leverage=1) can't be liquidated until price hits 0. */}
                {result.liquidationPrice != null ? (
                  <>
                    <Stat label="Liquidation price" value={`$${formatNum(result.liquidationPrice, 2)}`} accent="red" />
                    <Stat label="Liq. distance" value={`${formatNum(result.liquidationDistancePct, 2)}%`} />
                  </>
                ) : (
                  <Stat label="Liquidation" value="N/A — spot" sub="No leverage, no liquidation" />
                )}
              </div>

              {/* Take Profit / Risk:Reward block — renders when user supplied a TP */}
              {result.takeProfit && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4">
                  <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-2 flex items-center gap-1">
                    <Target className="w-3 h-3" /> Risk : Reward
                  </p>
                  <div className="flex items-end gap-3 mb-3">
                    <p className="text-3xl font-bold text-emerald-400 font-mono leading-none">
                      1:{formatNum(result.takeProfit.riskRewardRatio, 2)}
                    </p>
                    <p className="text-xs text-gray-400 pb-1">risk : reward ratio</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <Stat label="Reward ($)" value={`+$${formatNum(result.takeProfit.rewardDollars, 2)}`} accent="emerald" />
                    <Stat label="TP distance" value={`${formatNum(result.takeProfit.tpDistancePct, 2)}%`} />
                    <Stat label="Break-even win rate" value={`${formatNum(result.takeProfit.breakEvenWinRate, 1)}%`} accent="amber" sub="Min win % to be profitable at this R:R" />
                    <Stat label="Reward : Risk" value={`+$${formatNum(result.takeProfit.rewardDollars, 0)} : -$${formatNum(result.maxRiskDollars, 0)}`} />
                  </div>
                  <p className="text-[10px] text-gray-500">{result.takeProfit.rrComment}</p>
                </div>
              )}

              {result.stopBeyondLiquidation && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 text-xs font-bold">Stop is BEYOND liquidation</p>
                    <p className="text-xs text-gray-300 mt-0.5">
                      You will be liquidated before your SL fills. Either reduce leverage or move stop closer to entry.
                    </p>
                  </div>
                </div>
              )}

              {result.kelly && (
                <div className="bg-violet-500/5 border border-violet-500/20 rounded-2xl p-4">
                  <p className="text-xs uppercase tracking-wider text-violet-400 font-semibold mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Kelly Criterion
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <Stat label="Full Kelly fraction" value={formatNum(result.kelly.kellyFraction, 4)} />
                    <Stat label="¼ Kelly suggestion" value={`$${formatNum(result.kelly.kellyDollars, 2)}`} accent="violet" />
                  </div>
                  <p className="text-[10px] text-gray-500">{result.kelly.kellyComment}</p>
                </div>
              )}
            </>
          )}

          {loading && <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Calculating…</div>}
        </div>
      </div>

      <div className="bg-dark-800/30 rounded-2xl border border-dark-700/30 p-4">
        <p className="text-[10px] text-gray-500 flex items-start gap-1.5">
          <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
          Liquidation price is approximate (excludes maintenance margin nuances and funding costs which exchanges add). Kelly assumes a Bernoulli win/loss distribution; real markets are fat-tailed, so use Quarter Kelly or smaller. None of this is financial advice — sizing risk is your responsibility.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, sub }) {
  const colorMap = {
    emerald: 'text-emerald-400',
    red: 'text-red-400',
    amber: 'text-amber-400',
    violet: 'text-violet-400',
  };
  return (
    <div className="bg-dark-700/30 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-bold font-mono mt-0.5 ${colorMap[accent] || 'text-white'}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-600 mt-0.5">{sub}</p>}
    </div>
  );
}
